import { useQuery } from '@apollo/client'
import { ErrorBoundary, Provider } from '@rollbar/react'

import { EnvConfig } from '../../configuration/contexts/ConfigurationContext'
import { useEnvConfig } from '../../configuration/hooks/useEnvConfig'
import { useUser } from '../../configuration/hooks/useUser'
import PATIENT_PROFILE_QUERY from '../graphql/patient/PatientProfileQuery.gql'
import PROVIDER_PROFILE_QUERY from '../graphql/provider/ProviderProfileQuery.gql'

const isEnvironmentAllowed = (envName: string): boolean => {
  const envs = ['demo', 'development', 'test']

  return !envs.includes(envName.toLowerCase())
}

const getRollbarConfig = (
  envConfig: EnvConfig,
  person?: { id: string; type: string },
) => {
  return {
    accessToken: envConfig.rollbarJsAccessToken!,
    captureUncaught: true,
    captureUnhandledRejections: true,
    enabled:
      isEnvironmentAllowed(envConfig.environmentName) &&
      !envConfig.fedrampEnabled,
    payload: {
      environment: envConfig.environmentName,
      client: {
        javascript: {
          code_version: envConfig.assetVersion,
          guess_uncaught_frames: true,
        },
      },
      person,
    },
    scrubFields: [
      'allergies',
      'history',
      'medications',
      'messageInput',
      'progressNote',
      'transferNote',
    ],
  }
}

// this `any` is to get around a TypeScript issue with Rollbar
const FallbackUI: any = () => <div>Something went wrong</div>

export const AccountExceptionNotifier = ({ children }) => {
  const envConfig = useEnvConfig()
  const rollbarConfig = getRollbarConfig(envConfig)
  return (
    <Provider config={rollbarConfig}>
      <ErrorBoundary fallbackUI={FallbackUI}>{children}</ErrorBoundary>
    </Provider>
  )
}

export const ExceptionNotifier = ({ children }) => {
  const { isProvider } = useUser()
  const envConfig = useEnvConfig()

  const { data } = useQuery(
    isProvider ? PROVIDER_PROFILE_QUERY : PATIENT_PROFILE_QUERY,
  )

  const profile: { credentialId: string; type: string } =
    data?.patientProfile?.profile ?? data?.providerProfile?.profile

  if (profile) {
    const rollbarConfig = getRollbarConfig(envConfig, {
      id: profile.credentialId,
      type: profile.type,
    })
    return (
      <Provider config={rollbarConfig}>
        <ErrorBoundary fallbackUI={FallbackUI}>{children}</ErrorBoundary>
      </Provider>
    )
  }
  return null
}
